import React, { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { Video, ArrowRight, Clock, IndianRupee } from "lucide-react";
import { toast } from "sonner";

/**
 * Phase 9 funnel — "Book video consultation" upsell shown to patients on a
 * completed case. Creates a new ₹2,000 video case linked to the parent case,
 * then routes the patient straight to the payment screen.
 */
export default function BookVideoFollowup({ consultationId, onBooked }) {
  const [busy, setBusy] = useState(false);

  const book = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/consultations/${consultationId}/book-video-followup`);
      toast.success("Video follow-up created — complete payment to confirm");
      onBooked?.(r.data?.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create follow-up");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-6 border-violet-200 bg-gradient-to-br from-violet-50/60 to-purple-50/30" data-testid="book-video-followup">
      <div className="flex items-start gap-3 flex-wrap">
        <span className="w-10 h-10 rounded-full bg-violet-100 text-violet-700 grid place-items-center shrink-0">
          <Video className="w-5 h-5"/>
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-lg font-semibold text-slate-900">Want a 20-minute HD video follow-up?</div>
          <div className="text-sm text-slate-600 mt-1">
            Speak directly with your specialist about progress, doubts, and next steps.
            Same doctor, same case context, no re-explaining.
          </div>
          <div className="mt-3 flex items-center gap-3 text-[12px] text-slate-500">
            <span className="inline-flex items-center gap-1"><IndianRupee className="w-3.5 h-3.5"/> 2,000 flat</span>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5"/> 24-hour scheduling</span>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1"><Video className="w-3.5 h-3.5"/> Built-in HD video</span>
          </div>
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={book} disabled={busy} className="bg-violet-700 hover:bg-violet-800" data-testid="book-video-btn">
          {busy ? "Creating booking…" : "Book video consultation"} <ArrowRight className="w-4 h-4 ml-2"/>
        </Button>
      </div>
    </Card>
  );
}
